import { Alert, Badge, Loader, Table, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

import { isEtfSectionError, useEtfContext } from './etf_common';

const EtfHoldings = () => {
    const { data, loading } = useEtfContext();

    if (loading && !data) return <Loader />;
    if (!data) return <Text c="dimmed">Waiting for the synced position — click <b>Refresh Data</b> if none has been pulled yet.</Text>;
    if (isEtfSectionError(data.holdings)) {
        return (
            <Alert color="red" icon={<IconAlertTriangle />} title="Holdings unavailable">
                {data.holdings.error}
            </Alert>
        );
    }

    const { holdings, asOf, updatedAt } = data.holdings;
    const totalMarketValue = holdings.reduce((sum, h) => sum + h.marketValue, 0);
    const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 3 });
    const pct = (n: number) => (totalMarketValue ? `${((n / totalMarketValue) * 100).toFixed(2)}%` : '—');

    return (
        <>
            <Text size="sm" c="dimmed" mb="xs">
                Position as of {asOf ?? 'unknown'} (updated {updatedAt ?? 'unknown'}) — live from Geneva, {holdings.length} lines
            </Text>
            <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Security</Table.Th>
                        <Table.Th>Ticker</Table.Th>
                        <Table.Th>Name</Table.Th>
                        <Table.Th ta="right">Latest Price</Table.Th>
                        <Table.Th ta="right">Quantity</Table.Th>
                        <Table.Th ta="right">Market Value</Table.Th>
                        <Table.Th ta="right">Weight</Table.Th>
                        <Table.Th>Currency</Table.Th>
                        <Table.Th ta="right">FX</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {holdings.map((h) => (
                        <Table.Tr key={h.security}>
                            <Table.Td>{h.security} {h.cash && <Badge color="gray" size="xs">CASH</Badge>}</Table.Td>
                            <Table.Td>{h.ticker}</Table.Td>
                            <Table.Td>{h.name}</Table.Td>
                            <Table.Td ta="right">{h.latestPrice.toFixed(3)}</Table.Td>
                            <Table.Td ta="right">{fmt(h.quantity)}</Table.Td>
                            <Table.Td ta="right">{fmt(h.marketValue)}</Table.Td>
                            <Table.Td ta="right">{pct(h.marketValue)}</Table.Td>
                            <Table.Td>{h.currency}</Table.Td>
                            <Table.Td ta="right">{h.fxRate}</Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
                <Table.Tfoot>
                    <Table.Tr fw={700}>
                        <Table.Td colSpan={5}>Total</Table.Td>
                        <Table.Td ta="right">{fmt(totalMarketValue)}</Table.Td>
                        <Table.Td ta="right">100.00%</Table.Td>
                        <Table.Td colSpan={2} />
                    </Table.Tr>
                </Table.Tfoot>
            </Table>
        </>
    );
};

export default EtfHoldings;
